import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
    Sparkles,
    Wand2,
    FileText,
    ArrowRight,
    Check,
    TrendingUp,
    Shield,
    Layers,
    Brain,
    Menu,
    X,
} from "lucide-react";
import CustomCursor from "@/components/CustomCursor";
import AnimatedBackground from "@/components/AnimatedBackground";
import ScoreCircle from "@/components/ScoreCircle";

const PLANS = [
    {
        id: "free",
        name: "Free",
        price: "0€",
        period: "/forever",
        accent: "from-aura-muted to-aura-dim",
        features: [
            "1 Identity Score / month",
            "1 bio generation",
            "Basic report (score + 3 insights)",
            "GDPR data export",
        ],
        cta: "Start free",
    },
    {
        id: "premium",
        name: "Premium",
        price: "9.99€",
        period: "/month",
        accent: "from-aura-violet to-aura-glow",
        highlight: true,
        features: [
            "10 Identity Scores / month",
            "Unlimited bio generation",
            "Full AI report (PDF + web)",
            "7-day action plan",
            "Report history",
        ],
        cta: "Go Premium",
    },
    {
        id: "pro",
        name: "Pro",
        price: "19.99€",
        period: "/month",
        accent: "from-aura-cyan to-aura-blue",
        features: [
            "Unlimited Identity Scores",
            "Everything in Premium",
            "Social Audit",
            "Content Planner",
            "A/B Testing bios",
            "Priority access to new features",
        ],
        cta: "Get Pro",
    },
];

const FEATURES = [
    {
        i: Brain,
        t: "GPT-5.2 Engine",
        d: "Trained on millions of high-performing creator profiles. Decodes your signal in 60 seconds.",
        color: "violet",
    },
    {
        i: Sparkles,
        t: "Identity Score",
        d: "0–100 neural rating across 6 dimensions: clarity, credibility, consistency, attractiveness, visibility, professionalism.",
        color: "cyan",
    },
    {
        i: Wand2,
        t: "Bio Forge",
        d: "Magnetic bios calibrated per platform — Instagram, LinkedIn, TikTok. One click, one copy.",
        color: "pink",
    },
    {
        i: FileText,
        t: "7-Day Plan",
        d: "Concrete daily actions to amplify your aura. No fluff, no waiting.",
        color: "violet",
    },
    {
        i: TrendingUp,
        t: "Growth Strategy",
        d: "Strategic narrative tied to your goal — creator, influencer, professional, entrepreneur.",
        color: "cyan",
    },
    {
        i: Shield,
        t: "GDPR-Native",
        d: "Export everything. Delete anything. Your data, your terms.",
        color: "violet",
    },
];

const TESTIMONIALS = [
    { name: "Léa M.", role: "Content creator", quote: "AURA réécrit ma bio mieux que mon copywriter. +38% de profil clicks en 2 semaines." },
    { name: "Karim B.", role: "AI consultant", quote: "Le plan 7 jours m'a débloqué LinkedIn. Plus de 1k followers en 3 semaines." },
    { name: "Sofia R.", role: "Founder", quote: "C'est comme avoir un brand strategist en standby. Calme, précis, magnétique." },
    { name: "Tom L.", role: "Indie hacker", quote: "Le seul outil qui me dit pourquoi mon profil convertit pas. ROI immédiat." },
    { name: "Anaïs P.", role: "Coach", quote: "J'ai compris en 60s ce que j'avais raté en 2 ans de branding personnel." },
];

export default function Landing() {
    const [mobileOpen, setMobileOpen] = useState(false);

    return (
        <div className="min-h-screen relative overflow-hidden bg-aura-bg">
            <AnimatedBackground />
            <CustomCursor />

            {/* Menu mobile — rendu au niveau racine pour éviter les z-index imbriqués */}
            <AnimatePresence>
                {mobileOpen && (
                    <motion.div
                        key="mobile-menu"
                        initial={{ opacity: 0, y: -8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -8 }}
                        transition={{ duration: 0.2, ease: "easeOut" }}
                        style={{ zIndex: 9999 }}
                        className="fixed top-[88px] left-4 right-4 rounded-2xl glass-strong border border-aura-violet/15 shadow-2xl overflow-hidden"
                    >
                        <nav className="flex flex-col p-4 gap-1 font-dm-sans text-sm">
                            <a href="#features" onClick={() => setMobileOpen(false)} className="px-4 py-3 rounded-xl text-aura-body hover:text-aura-text hover:bg-aura-violet/8 transition">Features</a>
                            <a href="#pricing" onClick={() => setMobileOpen(false)} className="px-4 py-3 rounded-xl text-aura-body hover:text-aura-text hover:bg-aura-violet/8 transition">Pricing</a>
                            <a href="#testimonials" onClick={() => setMobileOpen(false)} className="px-4 py-3 rounded-xl text-aura-body hover:text-aura-text hover:bg-aura-violet/8 transition">Reviews</a>
                            <div className="my-2 border-t border-aura-violet/10" />
                            <Link
                                to="/login"
                                onClick={() => setMobileOpen(false)}
                                className="px-4 py-3 rounded-xl text-aura-body hover:text-aura-text hover:bg-aura-violet/8 transition"
                            >
                                Sign in
                            </Link>
                            <Link
                                to="/register"
                                onClick={() => setMobileOpen(false)}
                                className="mt-1 px-4 py-3 rounded-xl bg-gradient-to-r from-aura-violet to-aura-glow text-white font-medium text-center glow-violet-soft btn-ripple"
                            >
                                Get started
                            </Link>
                        </nav>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Header */}
            <header className="relative z-10 flex items-center justify-between px-6 lg:px-12 py-6">
                <Link to="/" className="flex items-center gap-3">
                    <img
                        src="/asset/generated-image__48_-removebg-preview.png"
                        alt="MirrorDigital Logo"
                        className="w-16 h-16 object-contain drop-shadow-[0_0_15px_rgba(124,58,237,0.55)]"
                    />
                    <div>
                        <div className="font-syne font-bold text-base leading-none">MirrorDigital</div>
                        <div className="font-jetbrains-mono text-[9px] tracking-[0.3em] text-aura-muted leading-none mt-0.5">AURA</div>
                    </div>
                </Link>

                {/* Nav desktop */}
                <nav className="hidden md:flex items-center gap-8 text-sm font-dm-sans">
                    <a href="#features" className="text-aura-body hover:text-aura-text transition">Features</a>
                    <a href="#pricing" className="text-aura-body hover:text-aura-text transition">Pricing</a>
                    <a href="#testimonials" className="text-aura-body hover:text-aura-text transition">Reviews</a>
                </nav>

                {/* Boutons desktop */}
                <div className="hidden md:flex gap-3 items-center">
                    <Link to="/login" className="text-sm text-aura-body hover:text-aura-text transition" data-testid="landing-login-link">
                        Sign in
                    </Link>
                    <Link
                        to="/register"
                        className="text-sm px-4 py-2 rounded-lg bg-gradient-to-r from-aura-violet to-aura-glow text-white font-dm-sans font-medium glow-violet-soft btn-ripple"
                        data-testid="landing-register-cta"
                    >
                        Get started
                    </Link>
                </div>

                {/* Bouton hamburger mobile */}
                <button
                    onClick={() => setMobileOpen((v) => !v)}
                    aria-label={mobileOpen ? "Fermer le menu" : "Ouvrir le menu"}
                    className="md:hidden p-2 rounded-lg text-aura-text hover:bg-aura-violet/10 transition relative z-[9999]"
                >
                    {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                </button>
            </header>

            {/* Hero */}
            <main className="relative z-10 max-w-6xl mx-auto px-6 pt-12 pb-24 text-center">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="inline-flex items-center gap-2 px-3.5 py-1.5 mb-7 rounded-full bg-white border border-aura-violet/20 shadow-sm font-jetbrains-mono text-[11px] uppercase tracking-[0.2em] text-aura-violet"
                >
                    <Sparkles className="w-3 h-3" /> AI Personal Branding · GPT-5.2
                </motion.div>
                <motion.h1
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className="font-syne text-5xl sm:text-7xl font-bold leading-[1.02] tracking-tight mb-6 text-aura-text"
                >
                    Your identity,
                    <br />
                    <span className="text-grad-violet">engineered for impact.</span>
                </motion.h1>
                <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="font-dm-sans text-aura-body text-lg max-w-2xl mx-auto mb-10 leading-relaxed"
                >
                    AURA decodes how the world perceives you across every platform, then engineers
                    your digital signal — score, bios, action plan, growth strategy — in 60 seconds.
                </motion.p>
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="flex flex-col sm:flex-row gap-3 justify-center mb-12"
                >
                    <Link
                        to="/register"
                        className="px-7 py-3.5 rounded-xl bg-gradient-to-r from-aura-violet to-aura-glow text-white font-dm-sans font-medium glow-violet btn-ripple flex items-center justify-center gap-2 hover:shadow-[0_15px_40px_-10px_rgba(124,58,237,0.6)] transition"
                        data-testid="landing-hero-cta"
                    >
                        Compute my AURA <ArrowRight className="w-4 h-4" />
                    </Link>
                    <a
                        href="#pricing"
                        className="px-7 py-3.5 rounded-xl border border-aura-violet/30 bg-white/60 text-aura-text font-dm-sans font-medium hover:bg-white transition btn-ripple"
                    >
                        See pricing
                    </a>
                </motion.div>

                {/* Hero showcase mockup */}
                <motion.div
                    initial={{ opacity: 0, y: 40 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4, duration: 0.7 }}
                    className="relative max-w-4xl mx-auto"
                >
                    <div className="absolute -inset-1 bg-gradient-to-r from-aura-violet/20 to-aura-cyan/20 rounded-2xl blur-xl" />
                    <div className="relative glass-strong p-8 lg:p-10 grid lg:grid-cols-[220px_1fr] gap-8 items-center text-left">
                        <div className="relative flex items-center justify-center">
                            <motion.div
                                animate={{
                                    rotateY: [0, 15, 0, -15, 0],
                                    rotateX: [0, 5, 0, -5, 0],
                                    scale: [1, 1.03, 1],
                                }}
                                transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
                                style={{ transformStyle: "preserve-3d", perspective: 800 }}
                            >
                                <ScoreCircle score={84} size={200} label="Demo signal" />
                            </motion.div>
                            <motion.div
                                className="absolute w-[200px] h-[200px] rounded-full border border-aura-violet/30"
                                animate={{ scale: [1, 1.2, 1], opacity: [0.4, 0, 0.4] }}
                                transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
                            />
                            <motion.div
                                className="absolute w-[200px] h-[200px] rounded-full border border-aura-cyan/20"
                                animate={{ scale: [1, 1.35, 1], opacity: [0.3, 0, 0.3] }}
                                transition={{ duration: 8, delay: 1, repeat: Infinity, ease: "easeInOut" }}
                            />
                        </div>
                        <div>
                            <motion.div
                                initial={{ opacity: 0, x: -10 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: 0.8 }}
                                className="flex items-center gap-2 mb-3"
                            >
                                <motion.div
                                    className="w-1.5 h-1.5 rounded-full bg-aura-violet"
                                    animate={{ opacity: [1, 0.3, 1] }}
                                    transition={{ duration: 1.5, repeat: Infinity }}
                                />
                                <span className="font-jetbrains-mono text-[10px] uppercase tracking-[0.3em] text-aura-violet">
                                    // SYSTEM PERCEPTION
                                </span>
                            </motion.div>
                            <motion.p
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 1, duration: 0.6 }}
                                className="font-syne text-xl lg:text-2xl text-aura-text leading-snug mb-6"
                            >
                                "A sharp, focused operator in AI productivity — clear voice, consistent rhythm, ready to scale beyond their niche."
                            </motion.p>
                            <div className="grid grid-cols-3 gap-3">
                                {[
                                    { l: "Clarity", v: 9.1, delay: 1.2 },
                                    { l: "Credibility", v: 7.8, delay: 1.4 },
                                    { l: "Visibility", v: 6.4, delay: 1.6 },
                                ].map((s) => (
                                    <motion.div
                                        key={s.l}
                                        initial={{ opacity: 0, y: 15 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: s.delay, duration: 0.5 }}
                                        whileHover={{ scale: 1.05, borderColor: "rgba(124,58,237,0.4)" }}
                                        className="bg-aura-elevate/60 border border-aura-violet/10 rounded-lg p-2.5 cursor-default transition-colors"
                                    >
                                        <div className="font-jetbrains-mono text-[10px] uppercase tracking-wider text-aura-muted">{s.l}</div>
                                        <motion.div
                                            initial={{ opacity: 0 }}
                                            animate={{ opacity: 1 }}
                                            transition={{ delay: s.delay + 0.2 }}
                                            className="font-syne text-lg text-aura-text"
                                        >
                                            {s.v}
                                        </motion.div>
                                    </motion.div>
                                ))}
                            </div>
                        </div>
                    </div>
                </motion.div>
            </main>

            {/* Logo strip / trust */}
            <section className="relative z-10 max-w-6xl mx-auto px-6 pb-20">
                <div className="text-center font-jetbrains-mono text-[10px] uppercase tracking-[0.3em] text-aura-muted mb-6">
                    // TRUSTED BY 12,000+ CREATORS WORLDWIDE
                </div>
                <div className="flex flex-wrap justify-center items-center gap-8 opacity-60">
                    {["INDIE HACKERS", "PRODUCT HUNT", "TECHCRUNCH", "MAKERS", "STARTUP DIGEST", "Y COMBINATOR"].map((l) => (
                        <div key={l} className="font-syne text-xs tracking-[0.2em] text-aura-body">{l}</div>
                    ))}
                </div>
            </section>

            {/* Features */}
            <section id="features" className="relative z-10 max-w-6xl mx-auto px-6 py-20">
                <div className="text-center mb-14">
                    <div className="font-jetbrains-mono text-[11px] uppercase tracking-[0.3em] text-aura-violet mb-3">
                        // CAPABILITIES
                    </div>
                    <h2 className="font-syne text-4xl sm:text-5xl font-bold mb-3 text-aura-text">
                        Every layer of your brand, <span className="text-grad-violet">decoded</span>.
                    </h2>
                    <p className="font-dm-sans text-aura-body max-w-xl mx-auto">
                        From perception to publication. Six AI modules, one signal.
                    </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                    {FEATURES.map((f, i) => (
                        <motion.div
                            key={f.t}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-80px" }}
                            transition={{ delay: i * 0.05 }}
                            whileHover={{ y: -4 }}
                            className="glass p-6 group cursor-pointer"
                        >
                            <div
                                className={`w-10 h-10 rounded-xl flex items-center justify-center mb-4 bg-gradient-to-br ${
                                    f.color === "violet"
                                        ? "from-aura-violet/15 to-aura-glow/15 text-aura-violet"
                                        : f.color === "cyan"
                                          ? "from-aura-cyan/15 to-aura-blue/15 text-aura-cyan"
                                          : "from-aura-pink/15 to-aura-violet/15 text-aura-pink"
                                } group-hover:scale-110 transition`}
                            >
                                <f.i className="w-5 h-5" strokeWidth={1.8} />
                            </div>
                            <h3 className="font-syne text-xl font-semibold mb-2 text-aura-text">{f.t}</h3>
                            <p className="text-sm text-aura-body leading-relaxed">{f.d}</p>
                        </motion.div>
                    ))}
                </div>
            </section>

            {/* Pricing */}
            <section id="pricing" className="relative z-10 max-w-6xl mx-auto px-6 py-20">
                <div className="text-center mb-14">
                    <div className="font-jetbrains-mono text-[11px] uppercase tracking-[0.3em] text-aura-violet mb-3">
                        // PRICING
                    </div>
                    <h2 className="font-syne text-4xl sm:text-5xl font-bold mb-3 text-aura-text">
                        Calibrate your <span className="text-grad-violet">access</span>.
                    </h2>
                    <p className="font-dm-sans text-aura-body">Start free. Upgrade when the signal scales.</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
                    {PLANS.map((p, i) => (
                        <motion.div
                            key={p.id}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: i * 0.08 }}
                            className={`relative glass p-7 flex flex-col ${
                                p.highlight
                                    ? "ring-2 ring-aura-violet/40 shadow-[0_20px_60px_-20px_rgba(124,58,237,0.4)] md:-translate-y-3"
                                    : ""
                            }`}
                        >
                            {p.highlight && (
                                <div className="absolute -top-3 left-1/2 -translate-x-1/2 font-jetbrains-mono text-[10px] uppercase tracking-[0.2em] px-3 py-1 rounded-full bg-gradient-to-r from-aura-violet to-aura-glow text-white shadow-md">
                                    Most popular
                                </div>
                            )}
                            <div className="font-syne text-2xl font-bold text-aura-text mb-1">{p.name}</div>
                            <div className="flex items-baseline gap-1 mb-5">
                                <span className={`font-syne text-5xl font-bold bg-gradient-to-r ${p.accent} bg-clip-text text-transparent`}>
                                    {p.price}
                                </span>
                                <span className="text-sm text-aura-muted">{p.period}</span>
                            </div>
                            <ul className="space-y-2.5 mb-7 flex-1">
                                {p.features.map((f) => (
                                    <li key={f} className="flex items-start gap-2.5 text-sm text-aura-body">
                                        <Check className="w-4 h-4 text-aura-violet shrink-0 mt-0.5" strokeWidth={2.5} />
                                        {f}
                                    </li>
                                ))}
                            </ul>
                            <Link
                                to="/register"
                                className={`text-center px-5 py-3 rounded-lg font-dm-sans font-medium btn-ripple transition ${
                                    p.highlight
                                        ? "bg-gradient-to-r from-aura-violet to-aura-glow text-white glow-violet-soft hover:shadow-[0_10px_30px_-10px_rgba(124,58,237,0.6)]"
                                        : "border border-aura-violet/30 text-aura-text hover:bg-aura-violet/5"
                                }`}
                                data-testid={`pricing-cta-${p.id}`}
                            >
                                {p.cta}
                            </Link>
                        </motion.div>
                    ))}
                </div>
            </section>

            {/* Testimonials */}
            <section id="testimonials" className="relative z-10 py-20 overflow-hidden">
                <div className="text-center mb-12 px-6">
                    <div className="font-jetbrains-mono text-[11px] uppercase tracking-[0.3em] text-aura-violet mb-3">
                        // SOCIAL PROOF
                    </div>
                    <h2 className="font-syne text-4xl sm:text-5xl font-bold mb-3 text-aura-text">
                        They've reset their <span className="text-grad-violet">aura</span>.
                    </h2>
                </div>
                <div className="relative max-w-7xl mx-auto">
                    <div className="flex gap-5 animate-marquee" style={{ width: "200%" }}>
                        {[...TESTIMONIALS, ...TESTIMONIALS].map((t, i) => (
                            <div key={i} className="glass p-6 min-w-[300px] max-w-[320px]">
                                <Layers className="w-4 h-4 text-aura-violet mb-3" />
                                <p className="text-sm text-aura-body italic mb-4 leading-relaxed">"{t.quote}"</p>
                                <div className="flex items-center gap-2.5">
                                    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-aura-violet/30 to-aura-cyan/30 flex items-center justify-center font-syne text-sm font-bold text-aura-text">
                                        {t.name[0]}
                                    </div>
                                    <div>
                                        <div className="font-syne text-sm font-semibold text-aura-text">{t.name}</div>
                                        <div className="text-xs text-aura-muted">{t.role}</div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Final CTA */}
            <section className="relative z-10 max-w-4xl mx-auto px-6 py-24 text-center">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    className="glass-strong p-10 lg:p-14"
                >
                    <Sparkles className="w-7 h-7 text-aura-violet mx-auto mb-5" />
                    <h2 className="font-syne text-4xl sm:text-5xl font-bold mb-4 text-aura-text">
                        Ready to <span className="text-grad-violet">reset</span> your signal?
                    </h2>
                    <p className="text-aura-body max-w-md mx-auto mb-7">
                        Free tier — no card required. Your first AURA in 60 seconds.
                    </p>
                    <Link
                        to="/register"
                        className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-gradient-to-r from-aura-violet via-aura-glow to-aura-cyan text-white font-dm-sans font-medium glow-violet btn-ripple"
                        data-testid="landing-bottom-cta"
                    >
                        Activate MirrorDigital <ArrowRight className="w-4 h-4" />
                    </Link>
                </motion.div>
            </section>

            {/* Footer */}
            <footer className="relative z-10 border-t border-aura-violet/10 py-8 px-6">
                <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
                    <div className="flex items-center gap-2 text-xs text-aura-muted font-jetbrains-mono">
                        © 2026 MirrorDigital · Built for the next generation of creators
                    </div>
                    <div className="flex gap-5 text-xs text-aura-muted">
                        <Link to="/privacy" className="hover:text-aura-text transition">Privacy</Link>
                        <Link to="/legal" className="hover:text-aura-text transition">Legal</Link>
                        <Link to="/contact" className="hover:text-aura-text transition">Contact</Link>
                    </div>
                </div>
            </footer>
        </div>
    );
}